import * as Sentry from '@sentry/react';

/**
 * Sentry Configuration
 * Centralizes error monitoring setup
 */

/**
 * Initialize Sentry error monitoring
 * Call this early in the application lifecycle
 */
export const initSentry = () => {
  // Only initialize in production or if explicitly enabled
  const shouldInitialize = 
    import.meta.env.PROD || 
    import.meta.env.VITE_SENTRY_ENABLED === 'true';

  if (!shouldInitialize) {
    console.log('Sentry disabled in development mode');
    return;
  }

  const dsn = import.meta.env.VITE_SENTRY_DSN;
  
  if (!dsn) {
    console.warn('Sentry DSN not configured. Set VITE_SENTRY_DSN environment variable.');
    return;
  }

  Sentry.init({
    dsn,
    environment: import.meta.env.MODE || 'production',
    release: import.meta.env.VITE_SENTRY_RELEASE || import.meta.env.VITE_APP_VERSION,
    
    // Performance Monitoring
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        // Mask all text content for privacy
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    
    // Capture 10% of transactions for performance monitoring
    tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,
    
    // Session Replay - capture 10% of sessions
    replaysSessionSampleRate: 0.1,
    
    // Session Replay - capture 100% of sessions with errors
    replaysOnErrorSampleRate: 1.0,
    
    // Don't send errors from dev mode unless explicitly enabled
    beforeSend(event, hint) {
      // Filter out development errors
      if (!import.meta.env.PROD && import.meta.env.VITE_SENTRY_ENABLED !== 'true') {
        return null;
      }
      
      // Add custom context
      event.tags = {
        ...event.tags,
        app_version: import.meta.env.VITE_APP_VERSION,
        build_time: import.meta.env.VITE_BUILD_TIME,
      };
      
      return event;
    },
    
    // Ignore specific errors
    ignoreErrors: [
      // Browser extensions
      'top.GLOBALS',
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      // Network errors that are expected
      'NetworkError',
      'Failed to fetch',
      'Load failed',
      // Cancelled requests
      'AbortError',
      'Request aborted',
    ],
    
    // Don't capture events from these URLs
    denyUrls: [
      // Browser extensions
      /extensions\//i,
      /^chrome:\/\//i,
      /^chrome-extension:\/\//i,
    ],
  });

  console.log(`Sentry initialized - Environment: ${import.meta.env.MODE}, Release: ${import.meta.env.VITE_SENTRY_RELEASE || 'unknown'}`);
};

/**
 * Manually capture an exception
 */
export const captureException = (error: Error, context?: Record<string, any>) => {
  if (context) {
    Sentry.setContext('additional', context);
  }
  Sentry.captureException(error);
};

/**
 * Manually capture a message
 */
export const captureMessage = (message: string, level: Sentry.SeverityLevel = 'info') => {
  Sentry.captureMessage(message, level);
};

/**
 * Set user context for error tracking
 */
export const setUserContext = (user: { id: string; email?: string; username?: string }) => {
  Sentry.setUser(user);
};

/**
 * Clear user context (e.g., on logout)
 */
export const clearUserContext = () => {
  Sentry.setUser(null);
};

/**
 * Add breadcrumb for debugging
 */
export const addBreadcrumb = (message: string, data?: Record<string, any>) => {
  Sentry.addBreadcrumb({
    message,
    data,
    level: 'info',
    timestamp: Date.now() / 1000,
  });
};

/**
 * Set custom tags for filtering
 */
export const setTags = (tags: Record<string, string>) => {
  Object.entries(tags).forEach(([key, value]) => {
    Sentry.setTag(key, value);
  });
};

/**
 * Start a performance measurement
 */
export const startPerformanceMeasure = (name: string) => {
  return Sentry.startSpan({
    name,
    op: 'custom',
  }, (span) => span);
};

export { Sentry };
